import { useState, useEffect } from 'react';

function fmt$(n) {
  const v = parseFloat(n ?? 0);
  return v === Math.round(v)
    ? '$' + Math.round(v).toLocaleString()
    : '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function baseBasisLabel(l) {
  if (l.basis === 'hours') return `${parseFloat(l.quantity || 0).toFixed(2)} h × ${fmt$(l.rate_or_amount)}`;
  if (l.basis === 'days')  return `${l.quantity} day${l.quantity === 1 ? '' : 's'} × ${fmt$(l.rate_or_amount)}`;
  if (l.basis === 'project') return 'Fixed project';
  if (l.basis === 'reimbursement') return l.note || 'Expense reimbursement';
  if (l.basis === 'hybrid') return l.note || 'Hybrid';
  if (l.basis === 'percent') return l.note || '% of client rate';
  return 'Flat monthly';
}

const basisLabel = (l) => l.unpaid_note ? `${baseBasisLabel(l)} · ${l.unpaid_note}` : baseBasisLabel(l);

const STATUS_COLORS = {
  draft: '#94a3b8', submitted: '#63d3f7', approved: '#68d391',
  disputed: '#f6c26b', void: '#94a3b8', confirmed: '#68d391',
};

function invNo(st) {
  const iss = st.issuer_snapshot || {};
  const prefix = iss.invoice_number_prefix || 'CINV';
  return `${prefix}-${st.period}-${String(st.id).slice(0, 8)}`;
}

export default function PublicContractorInvoice({ token }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid invoice link.'); setLoading(false); return; }
    // Edge function takes ?token= query param (GET request).
    const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-public-contractor-invoice?token=${encodeURIComponent(token)}`;
    fetch(url, {
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      },
    })
      .then(r => r.json())
      .then(res => {
        if (!res?.ok) setError('Invoice not found or link has expired.');
        else setData(res);
        setLoading(false);
      })
      .catch(() => { setError('Could not load invoice.'); setLoading(false); });
  }, [token]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif', color: '#64748b', fontSize: '14px' }}>
      Loading invoice…
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Invoice Not Found</div>
        <div style={{ fontSize: '14px', color: '#64748b' }}>{error}</div>
      </div>
    </div>
  );

  const { statement: st, org } = data;
  const iss = st.issuer_snapshot || {};
  const periodLabel = new Date(st.period + '-02').toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const num = invNo(st);
  const items = st.line_items || [];
  const statusColor = STATUS_COLORS[st.status] || '#94a3b8';
  const statusLabel = st.status ? st.status.charAt(0).toUpperCase() + st.status.slice(1) : '';
  const issAddrLines = [
    iss.address_line1, iss.address_line2,
    ([iss.city, iss.state].filter(Boolean).join(', ') + (iss.zip ? ' ' + iss.zip : '')).trim() || null,
    iss.country,
  ].filter(Boolean);

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'Arial, Helvetica, sans-serif', padding: '32px 16px' }}>

      {/* Print styles: hide toolbar + gray background, keep invoice white */}
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff !important; } }`}</style>

      {/* Toolbar — hidden when printing */}
      <div className="no-print" style={{ maxWidth: '760px', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '13px', color: '#64748b' }}>
          <strong style={{ color: '#1e293b' }}>{iss.name || 'Contractor'}</strong> · {num}
          <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', background: `${statusColor}22`, color: statusColor }}>
            {statusLabel}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handlePrint}
            style={{ padding: '8px 18px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
            🖨 Print / Save as PDF
          </button>
          {iss.payment_link && (
            <a href={iss.payment_link} target="_blank" rel="noreferrer"
              style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#111D39', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700', textDecoration: 'none', display: 'inline-block' }}>
              Pay Now
            </a>
          )}
        </div>
      </div>

      {/* Invoice card */}
      <div style={{ maxWidth: '760px', margin: '0 auto', background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <div style={{ background: '#111D39', padding: '24px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {iss.logo_url && <img src={iss.logo_url} alt={iss.name || ''} style={{ display: 'block', maxHeight: '48px', maxWidth: '140px', objectFit: 'contain', marginBottom: '8px' }} />}
            <div style={{ color: iss.logo_url ? '#ffffff' : '#00A0C8', fontSize: '20px', fontWeight: '800' }}>{iss.name || 'Contractor'}</div>
            <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>Invoice</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>{num}</div>
            <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{periodLabel}</div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '40px' }}>

          {/* Issuer / Recipient */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '36px', paddingBottom: '28px', borderBottom: '2px solid #e2e8f0', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>From</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{iss.name || ''}</div>
              {issAddrLines.map((l, i) => <div key={i} style={{ fontSize: '12px', color: '#64748b' }}>{l}</div>)}
            </div>
            <div style={{ flex: 1, textAlign: 'right' }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Bill To</div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{org?.name || ''}</div>
            </div>
          </div>

          {/* Period */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
            <table style={{ fontSize: '13px', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 20px 4px 0', color: '#94a3b8' }}>Period</td>
                  <td style={{ padding: '4px 0', fontWeight: '600', color: '#1e293b' }}>{periodLabel}</td>
                </tr>
                {st.submitted_at && (
                  <tr>
                    <td style={{ padding: '4px 20px 4px 0', color: '#94a3b8' }}>Submitted</td>
                    <td style={{ padding: '4px 0', fontWeight: '600', color: '#1e293b' }}>
                      {new Date(st.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Line items */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Client</th>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Basis</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', width: '100px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l, i) => (
                <tr key={i}>
                  <td style={{ padding: '12px 0', fontSize: '13px', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>{l.client_name || ''}</td>
                  <td style={{ padding: '12px 0', fontSize: '12px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{basisLabel(l)}</td>
                  <td style={{ padding: '12px 0', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>{fmt$(l.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '32px' }}>
            <div style={{ minWidth: '240px', borderTop: '2px solid #1e293b', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b' }}>Total</span>
              <span style={{ fontSize: '26px', fontWeight: 'bold', color: '#1e293b' }}>{fmt$(st.total)}</span>
            </div>
          </div>

          {/* Pay Now */}
          {iss.payment_link && (
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <a href={iss.payment_link} target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', background: '#111D39', color: '#fff', fontSize: '16px', fontWeight: '700', padding: '16px 48px', borderRadius: '8px', textDecoration: 'none' }}>
                Pay Now
              </a>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>Secure payment link</div>
            </div>
          )}

          {/* Footer */}
          <div style={{ textAlign: 'center', fontSize: '13px', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
            Thank you for your business.
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '760px', margin: '16px auto', textAlign: 'center', fontSize: '11px', color: '#94a3b8' }}>
        Powered by TimeKeep
      </div>
    </div>
  );
}
